import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, getDocs, orderBy, query } from 'firebase/firestore';
import { PFIFullLogo, BrandGradient } from '../components/common/PFILogo';
import { FlamingoLogo } from '../components/common/FlamingoBrand';
import { formatProjectName } from '../utils/projectUtils';
import { 
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  PhotoIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function CustomerProjectPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project
      const projectDoc = await getDoc(doc(db, 'projects', id));
      if (projectDoc.exists()) {
        setProject({ id: projectDoc.id, ...projectDoc.data() });
        
        // Load tasks for this project
        const tasksSnapshot = await getDocs(
          query(collection(db, 'projects', id, 'tasks'), orderBy('createdAt', 'desc'))
        );
        const tasksData = tasksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTasks(tasksData);
      } else {
        setError('Project not found');
      }
      
    } catch (error) {
      console.error('Error loading project:', error);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-gradient-to-r from-green-400 to-emerald-500 text-white';
      case 'in_progress':
        return 'bg-gradient-to-r from-blue-400 to-indigo-500 text-white';
      case 'planning':
        return 'bg-gradient-to-r from-orange-400 to-pink-500 text-white';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
    }
  };

  const getTaskStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'pending':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return dateObj.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateProgress = () => {
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-25 to-orange-25 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your project details...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-25 to-orange-25 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <PFIFullLogo size="md" className="mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The project you are looking for could not be found.'}</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              If you believe this is an error, please contact Pink Flamingo Interiors directly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-25 to-orange-25">
      {/* Header */}
      <div className="relative bg-gradient-to-r from-pink-600 via-pink-500 to-orange-400 text-white">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0,0 Q50,30 100,0 L100,100 L0,100 Z" fill="white"/>
          </svg>
        </div>
        <div className="relative px-6 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <PFIFullLogo size="md" className="text-white" />
              <div className="flex items-center space-x-4">
                <a
                  href="tel:+61400000000"
                  className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300"
                >
                  <PhoneIcon className="w-4 h-4 mr-2" />
                  Call Us
                </a>
                <a
                  href="mailto:hello@pinkflamingointeriors.com.au"
                  className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300"
                >
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  Email
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h1 className="text-4xl font-bold mb-4">{formatProjectName(project)}</h1>
                <p className="text-pink-100 text-xl mb-6">
                  Your interior design project with Pink Flamingo Interiors
                </p>
                
                <div className="flex items-center space-x-6 text-pink-100">
                  <div className="flex items-center">
                    <CalendarDaysIcon className="w-5 h-5 mr-2" />
                    Started: {formatDate(project.startDate)}
                  </div>
                  {project.expectedEndDate && (
                    <div className="flex items-center">
                      <ClockIcon className="w-5 h-5 mr-2" />
                      Expected: {formatDate(project.expectedEndDate)}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-pink-100">Project Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(project.status)}`}>
                    {project.status?.replace('_', ' ') || 'Planning'}
                  </span>
                </div>
                
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-pink-100">Progress</span>
                    <span className="text-white font-bold">{progress}%</span>
                  </div>
                  <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
                    <div 
                      className="bg-white rounded-full h-2 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                {project.budget > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-pink-100">Budget</span>
                    <span className="text-white font-bold">{formatCurrency(project.budget)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Project Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Project Description */}
            {project.projectDescription && (
              <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-8">
                <div className="flex items-center mb-6">
                  <DocumentTextIcon className="w-7 h-7 text-pink-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Project Overview</h2>
                </div>
                <div className="prose max-w-none">
                  <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap">
                    {project.projectDescription}
                  </p>
                </div>
              </div>
            )}

            {/* Tasks Progress */}
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8">
              <div className="flex items-center mb-6">
                <CheckCircleIcon className="w-7 h-7 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Project Tasks</h2>
              </div>
              
              {tasks.length > 0 ? (
                <div className="space-y-4">
                  {tasks.map((task, index) => (
                    <div key={task.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                          <span className="text-sm font-semibold text-pink-600">{index + 1}</span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900">{task.name}</h4>
                        {task.description && (
                          <p className="text-gray-600 text-sm mt-1">{task.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTaskStatusColor(task.status)}`}>
                          {task.status === 'in_progress' ? 'In Progress' : 
                           task.status === 'completed' ? 'Completed' : 
                           task.status || 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Task details will appear here as we progress with your project</p>
                </div>
              )}
            </div>

            {/* Project Timeline */}
            <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <CalendarDaysIcon className="w-7 h-7 text-green-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Project Timeline</h2>
                </div>
                <Link
                  to={`/customer/project/${id}/timeline`}
                  className="text-pink-600 hover:text-pink-700 font-medium"
                >
                  View Full Timeline →
                </Link>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Project Started</h4>
                    <p className="text-gray-600 text-sm">{formatDate(project.createdAt)}</p>
                    <p className="text-gray-500 text-sm mt-1">Initial consultation and planning phase begun</p>
                  </div>
                </div>
                
                {project.status !== 'planning' && (
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Design Phase</h4>
                      <p className="text-gray-500 text-sm mt-1">Creating your custom interior design solution</p>
                    </div>
                  </div>
                )}
                
                {project.expectedEndDate && (
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-orange-400 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Expected Completion</h4>
                      <p className="text-gray-600 text-sm">{formatDate(project.expectedEndDate)}</p>
                      <p className="text-gray-500 text-sm mt-1">Final installation and project completion</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Your Project Manager</h3>
              
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-400 to-orange-400 rounded-full mx-auto mb-3 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">PFI</span>
                </div>
                <h4 className="font-semibold text-gray-900">Pink Flamingo Interiors</h4>
                <p className="text-gray-500 text-sm">Your dedicated design team</p>
              </div>
              
              <div className="space-y-3">
                <a
                  href="tel:+61400000000"
                  className="w-full flex items-center justify-center px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <PhoneIcon className="w-4 h-4 mr-2" />
                  Call Now
                </a>
                <a
                  href="mailto:hello@pinkflamingointeriors.com.au"
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  Send Email
                </a>
                <button
                  className="w-full flex items-center justify-center px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                  disabled
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                  Live Chat (Coming Soon)
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Project Stats</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Tasks</span>
                  <span className="font-semibold">{tasks.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Completed</span>
                  <span className="font-semibold text-green-600">
                    {tasks.filter(t => t.status === 'completed').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">In Progress</span>
                  <span className="font-semibold text-blue-600">
                    {tasks.filter(t => t.status === 'in_progress').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Days Active</span>
                  <span className="font-semibold">
                    {Math.ceil((new Date() - new Date(project.createdAt.seconds * 1000)) / (1000 * 60 * 60 * 24))}
                  </span>
                </div>
              </div>
            </div>

            {/* Refresh Button */}
            <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
              <button
                onClick={() => {
                  setLoading(true);
                  loadData();
                }}
                className="w-full flex items-center justify-center px-4 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                disabled={loading}
              >
                <ArrowPathIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Updating...' : 'Refresh Project'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-500">
            <FlamingoLogo size="sm" className="text-pink-500" />
            <span className="text-sm">"Fun and Fabulous without the Fuss" ✨</span>
          </div>
        </div>
      </div>
    </div>
  );
}
